import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { 
  Package, 
  Key,
  ChefHat,
  ArrowRight,
  CheckCircle,
  Star,
  Globe,
  Award,
  Users,
  Droplets,
  Building2,
  Fish
} from 'lucide-react';
import { Link } from 'react-router-dom';


const Home = () => {

   // Données structurées JSON-LD pour hiérarchie géographique
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "LST OIL SA",
    "alternateName": "LST OIL Sénégal",
    "url": "https://www.lstoilsa.com",
    "logo": "https://www.lstoilsa.com/images/logo.png",
    "description": "LST OIL SA - Leader en hydrocarbures, logistique et produits alimentaires au Sénégal, Afrique de l'Ouest et international",
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "SN",
      "addressRegion": "Dakar",
      "addressLocality": "Dakar"
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "customer service",
      "availableLanguage": ["French", "English", "Wolof"]
    },
    "sameAs": [
      "https://www.linkedin.com/in/lst-oil-sa-697346386/",
      "https://www.instagram.com/lst.oil.sa/?__pwa=1%2F",
      "https://www.facebook.com/people/Lst-Oil-SA/pfbid02VAYXDp2RqkjCPjPcqCzTmfNy1gFxpysXX8Q2fGWJahAurNzrcDeXGq9VDXuz5R7Kl/"
    ],
    "areaServed": [
      { "@type": "Country", "name": "Sénégal" },
      { "@type": "Country", "name": "Mali" },
      { "@type": "Country", "name": "Côte d'Ivoire" },
      { "@type": "Country", "name": "Congo" },
      { "@type": "Country", "name": "Monde" }
    ],
    "knowsAbout": [
      "Hydrocarbures",
      "Logistique",
      "Produits alimentaires",
      "Import-Export Afrique",
      "Matériaux BTP Sénégal"
    ]
  };



  
  // Services 
  const services = [
    {
      icon: <Package className="w-8 h-8" />,
      title: "Logistique",
      description: "Solutions logistiques complètes incluant transport multimodal, douanes et stockage sécurisé.",
      color: "bg-blue-500",
      link: "#logistics",
      category: "logistics"
    },
    {
      icon: <Key className="w-8 h-8" />,
      title: "Hydrocarbures & BTP",
      description: "Expertise dans les hydrocarbures et matériaux BTP avec équipements industriels.",
      color: "bg-red-500",
      link: "#bases",
      category: "bases"
    },
    {
      icon: <ChefHat className="w-8 h-8" />,
      title: "Produits Alimentaires",
      description: "Denrées première nécessité et produits halieutiques de qualité supérieure.",
      color: "bg-green-500",
      link: "#alimentaires",
      category: "alimentaires"
    }
  ];

  // Fonction pour déterminer le lien selon le service
  const getServiceLink = (service) => {
    if (service.category === 'logistics') return '/services#logistics';
    if (service.category === 'bases') return '/services#bases';
    if (service.category === 'alimentaires') return '/services#alimentaires';
    return '/services';
  };

  const stats = [
    { number: "15+", label: "Années d'expérience" },
    { number: "500+", label: "Clients satisfaits" },
    { number: "15+", label: "Pays desservis" },
    { number: "24/7", label: "Support client" }
  ];

  const features = [
    "Logistique complète (transport, douanes, stockage)",
    "Hydrocarbures et produits pétroliers",
    "Produits alimentaires et halieutiques",
    "Matériaux BTP et équipements industriels", 
    "Solutions import/export personnalisées",
    "Réseau international de partenaires",
    "Consultance technique et administrative",
    "Support client 24/7"
  ];

  // Produits phares pour la section showcase
  const featuredProducts = [
    {
      icon: <Droplets className="w-8 h-8" />,
      title: "Hydrocarbures",
      description: "Essence premium, gazoil, huiles moteur de qualité internationale",
      color: "bg-red-500"
    },
    {
      icon: <ChefHat className="w-8 h-8" />,
      title: "Denrées Alimentaires", 
      description: "Sucre, riz de la vallée, huiles végétales certifiées",
      color: "bg-green-500"
    },
    {
      icon: <Fish className="w-8 h-8" />,
      title: "Produits Halieutiques",
      description: "Poissons frais, crevettes et fruits de mer de qualité",
      color: "bg-blue-500"
    },
    {
      icon: <Building2 className="w-8 h-8" />,
      title: "Matériaux BTP",
      description: "Ciment Portland, acier de construction, équipements industriels",
      color: "bg-orange-500"
    }
  ];

  return (
    <>
      
      <Helmet>
        <title>LST OIL SA - Hydrocarbures, Logistique & Produits Alimentaires Sénégal | Leader Afrique</title>
        <meta name="description" content="LST OIL SA : Votre partenaire de confiance pour les hydrocarbures, la logistique et les produits alimentaires au Sénégal, Afrique de l'Ouest et international." />
        <meta name="keywords" content="LST OIL SA, hydrocarbures Sénégal, logistique Dakar, produits alimentaires Sénégal, transport Afrique, BTP Sénégal, produits halieutiques, import-export Sénégal" />

        {/* Balises Open Graph */}
        <meta property="og:title" content="LST OIL SA - Leader Hydrocarbures & Logistique Sénégal" />
        <meta property="og:description" content="Solutions complètes en hydrocarbures, logistique et produits alimentaires au Sénégal, Afrique de l'Ouest et international" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.lstoilsa.com" />
        <meta property="og:image" content="https://www.lstoilsa.com/images/og-image-senegal.jpg" />
        <meta property="og:site_name" content="LST OIL SA Sénégal" />
        <meta property="og:locale" content="fr_SN" />

        {/* Balises Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="LST OIL SA - Hydrocarbures & Logistique Sénégal" />
        <meta name="twitter:description" content="Leader en hydrocarbures, logistique et produits alimentaires au Sénégal et en Afrique" />

        {/* Balises Géolocalisation */}
        <meta name="geo.region" content="SN-DK" />
        <meta name="geo.placename" content="Dakar, Sénégal" />
        <meta name="geo.position" content="14.716677;-17.467686" />
        <meta name="ICBM" content="14.716677, -17.467686" />

        {/* Données structurées */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-start justify-center overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat transition-all duration-1000 opacity-1 "
          style={{
            backgroundImage: "url('/images/background/warehouse.png')",
          }}
        ></div>
        
        {/* Overlay pour assombrir l'image et améliorer la lisibilité */}
  <div className="absolute inset-0 bg-gradient-to-br from-[#6BB8B1]/80 via-[#4B9D96]/70 to-[#3A7C76]/80"></div>
        
        {/* Pattern décoratif */}
        <div className="absolute inset-0 opacity-100">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23ffffff%22%20fill-opacity%3D%220.1%22%3E%3Ccircle%20cx%3D%2230%22%20cy%3D%2230%22%20r%3D%222%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')]"></div>
        </div>

        <div className="container mx-auto px-4 text-center text-white relative z-10 pt-10 md:pt-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl md:text-7xl font-bold mb-6 font-heading text-shadow-xl">
              LST OIL SA
            </h1>
            <p className="text-xl md:text-2xl mb-6 text-white max-w-4xl mx-auto text-shadow-lg">
              Votre partenaire de confiance pour les <span className="text-[#FFD700] font-semibold">Hydrocarbures</span>, 
              la <span className="text-[#FFD700] font-semibold">Logistique</span> et les <span className="text-[#FFD700] font-semibold">Services</span>
            </p>
            <p className="text-lg md:text-xl mb-8 text-white/95 max-w-3xl mx-auto text-shadow">
              Solutions complètes en hydrocarbures, produits alimentaires, halieutiques, 
              logistique internationale et transport multimodal
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/services"
                className="bg-[#cd3127] hover:bg-[#cd3127]/90 text-white font-bold py-4 px-8 rounded-lg text-lg transition-all duration-300 transform hover:scale-105 hover:shadow-xl inline-flex items-center gap-2 shadow-lg"
              >
                Nos Services
                <ArrowRight className="w-5 h-5" />
              </Link>
              <Link
                to="/products"
                className="border-2 border-white text-white hover:bg-white hover:text-[#4B9D96] font-bold py-4 px-8 rounded-lg text-lg transition-all duration-300 transform hover:scale-105 inline-flex items-center gap-2 bg-black/20 backdrop-blur-sm"
              >
                Nos Produits
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="absolute bottom-4 md:bottom-8 left-1/2 transform -translate-x-1/2 z-20 pointer-events-none"
          aria-hidden="true"
        >
          <div className="w-5 h-8 md:w-6 md:h-10 border-2 border-white/90 rounded-full flex justify-center items-start bg-black/20 backdrop-blur-sm">
            <div className="w-1 h-3 md:h-3 bg-white rounded-full mt-2"></div>
          </div>
        </motion.div>
      </section>

      {/* About Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 font-heading">
              Qui sommes-nous ?
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto">
              LST OIL SA est une entreprise leader spécialisée dans la commercialisation des hydrocarbures, 
              des produits alimentaires et halieutiques, ainsi que la logistique et les services. 
              Nous offrons des solutions complètes et innovantes dans nos secteurs clés.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h3 className="text-3xl font-bold text-gray-900 mb-6">
                Notre Mission
              </h3>
              <p className="text-lg text-gray-600 mb-6">
                Fournir des produits et services de qualité supérieure dans les hydrocarbures, 
                l'alimentaire et la logistique, en respectant les plus hauts standards de sécurité 
                et de fiabilité, tout en contribuant au développement durable de nos communautés.
              </p>
              <p className="text-lg text-gray-600 mb-8">
                Nous nous engageons à offrir des solutions personnalisées qui répondent 
                aux besoins spécifiques de chaque client, en nous appuyant sur notre 
                expertise technique et notre réseau international.
              </p>
              
              <div className="grid grid-cols-2 gap-4">
                {stats.map((stat, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="text-center p-4 bg-gray-50 rounded-lg"
                  >
                    <div className="text-3xl font-bold text-[#4B9D96] mb-2">{stat.number}</div>
                    <div className="text-sm text-gray-600">{stat.label}</div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="bg-gradient-to-br from-[#6BB8B1] to-[#3A7C76] rounded-2xl p-8 text-white">
                <h4 className="text-2xl font-bold mb-6">Nos Valeurs</h4>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-6 h-6 text-black" />
                    <span>Excellence et qualité de service</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-6 h-6 text-black" />
                    <span>Innovation et adaptation</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-6 h-6 text-black" />
                    <span>Responsabilité sociale</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-6 h-6 text-black" />
                    <span>Développement durable</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-6 h-6 text-black" />
                    <span>Confiance et transparence</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 font-heading">
              Nos Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Découvrez nos domaines d'expertise pour répondre à tous vos besoins
            </p>
          </motion.div>

          <div className="grid md:grid-cols-1 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl p-6 shadow-soft hover:shadow-medium transition-all duration-300 transform hover:-translate-y-2"
              >
                <div className={`${service.color} text-white p-3 rounded-lg w-fit mb-4`}>
                  {service.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {service.title}
                </h3>
                <p className="text-gray-600 mb-4">
                  {service.description}
                </p>
                <Link
                  to={getServiceLink(service)}
                  className="text-[#4B9D96] hover:text-[#4B9D96] font-semibold inline-flex items-center gap-2 transition-colors duration-300"
                >
                  En savoir plus
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Link
              to="/services"
              className="bg-[#4b9d96] hover:bg-[#4b9d96]/90 text-white font-bold py-4 px-8 rounded-lg text-lg transition-all duration-300 transform hover:scale-105 hover:shadow-lg inline-flex items-center gap-2"
            >
              Voir tous nos services
              <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Products Showcase Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 font-heading">
              Nos Produits
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Des produits de qualité dans nos secteurs d'expertise
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {featuredProducts.map((product, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-gray-50 rounded-xl p-6 text-center hover:shadow-md transition-shadow"
              >
                <div className={`${product.color} text-white p-3 rounded-lg w-fit mb-4 mx-auto`}>
                  {product.icon}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  {product.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {product.description}
                </p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Link
              to="/products"
              className="bg-[#cd3127] hover:bg-[#cd3127]/90 text-white font-bold py-4 px-8 rounded-lg text-lg transition-all duration-300 transform hover:scale-105 hover:shadow-lg inline-flex items-center gap-2"
            >
              Découvrir tous nos produits
              <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 font-heading">
              Pourquoi choisir LST OIL SA ?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Notre expertise, notre réseau international et notre engagement 
              envers l'excellence font de nous votre partenaire de confiance.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <div className="space-y-6">
                {features.map((feature, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="flex items-start gap-4"
                  >
                    <CheckCircle className="w-6 h-6 text-green-500 mt-1 flex-shrink-0" />
                    <span className="text-lg text-gray-700">{feature}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 gap-6"
            >
              <div className="text-center p-6 bg-blue-50 rounded-xl">
                <Globe className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                <h4 className="text-lg font-semibold text-gray-900 mb-2">Réseau International</h4>
                <p className="text-gray-600 text-sm">Présence mondiale avec des partenaires de confiance</p>
              </div>
              <div className="text-center p-6 bg-green-50 rounded-xl">
                <Award className="w-12 h-12 text-green-600 mx-auto mb-4" />
                <h4 className="text-lg font-semibold text-gray-900 mb-2">Qualité Certifiée</h4>
                <p className="text-gray-600 text-sm">Standards internationaux et certifications reconnues</p>
              </div>
              <div className="text-center p-6 bg-purple-50 rounded-xl">
                <Users className="w-12 h-12 text-purple-600 mx-auto mb-4" />
                <h4 className="text-lg font-semibold text-gray-900 mb-2">Équipe Expérimentée</h4>
                <p className="text-gray-600 text-sm">Professionnels qualifiés et dédiés à votre satisfaction</p>
              </div>
              <div className="text-center p-6 bg-orange-50 rounded-xl">
                <Star className="w-12 h-12 text-orange-600 mx-auto mb-4" />
                <h4 className="text-lg font-semibold text-gray-900 mb-2">Service Premium</h4>
                <p className="text-gray-600 text-sm">Accompagnement personnalisé et solutions sur mesure</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#4b9d96] to-[#3A7C76]">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 font-heading">
              Prêt à commencer ?
            </h2>
            <p className="text-xl text-white mb-8 max-w-2xl mx-auto">
              Contactez-nous dès aujourd'hui pour discuter de vos besoins 
              et découvrir comment nous pouvons vous aider à atteindre vos objectifs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="bg-[#cd3127] hover:bg-[#cd3127]/90 text-white font-bold py-4 px-8 rounded-lg text-lg transition-all duration-300 transform hover:scale-105 hover:shadow-lg inline-flex items-center gap-2"
              >
                Demander un Devis
                <ArrowRight className="w-5 h-5" />
              </Link>
              <Link
                to="/about"
                className="border-2 border-white text-white hover:bg-white hover:text-[#4b9d96] font-bold py-4 px-8 rounded-lg text-lg transition-all duration-300 transform hover:scale-105 inline-flex items-center gap-2"
              >
                En savoir plus
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default Home;
